import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Paper,
  Box,
  Button,
  Typography,
  Avatar,
  Divider,
  ListItemIcon,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton
} from '@mui/material';
import {
  QueryStats as AnalysisIcon,
  Group as GroupIcon,
  ReceiptLong as ReceiptLongIcon,
  Settings as SettingsIcon,
  Inventory2 as Inventory2Icon,
  ShoppingCart as ShoppingCartIcon,
  Devices as DevicesIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Mail as MailIcon,
  Chat as ChatIcon,
  AssignmentTurnedIn as TaskIcon,
  Close as CloseIcon,
  SettingsInputComponent as DeviceConfigIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Terminal as TerminalIcon,
  Sms as SmsIcon,
  MonitorHeart as MonitorHeartIcon,
  Memory as MemoryIcon,
  MarkEmailRead as MailIntakeIcon,
  Construction as EquipmentIcon,
  ReportProblem as ErrorsIcon,
  NotificationImportant as IssuesIcon,
  Troubleshoot as DiagnosisIcon,
  Assignment as JobCardsIcon,
  Build as MaintenanceIcon,
  Description as ContractsIcon,
  PeopleAlt as CustomersIcon,
  Place as SitesIcon,
  ListAlt as LogSheetsIcon,
  ExpandLess as MinimizeIcon,
  ExpandMore as MaximizeIcon,
  HelpOutline as HowToIcon,
  Campaign as CreatorNewsIcon,
  SignalCellularAlt as SignalCoverageIcon,
  MenuBook as PartsCatalogIcon,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import {
  isSuperAdmin as checkSuperAdmin, canViewPath, userDepartments, userHasExplicitPage, pageKeyForPath
} from '../../utils/permissions';
import {
  DEPARTMENTS, WORKSPACE_GROUPS, pathnameOf, isItemOnPath, isItemActive, userDeptKeys
} from '../../utils/departments';

// Icon-name (utils/departments.js) → component. Kept beside the imports so a
// new header only touches the departments file and this map.
const ICONS = {
  controlpanel: <MonitorHeartIcon />,
  equipment: <EquipmentIcon />,
  errors: <ErrorsIcon />,
  issues: <IssuesIcon />,
  diagnosis: <DiagnosisIcon />,
  jobcards: <JobCardsIcon />,
  maintenance: <MaintenanceIcon />,
  contracts: <ContractsIcon />,
  customers: <CustomersIcon />,
  sites: <SitesIcon />,
  logsheets: <LogSheetsIcon />,
  analysis: <AnalysisIcon />,
  billing: <ReceiptLongIcon />,
  devices: <DevicesIcon />,
  hardware: <MemoryIcon />,
  inventory: <Inventory2Icon />,
  procurement: <ShoppingCartIcon />,
  organization: <GroupIcon />,
  mail: <MailIcon />,
  mailintake: <MailIntakeIcon />,
  chat: <ChatIcon />,
  tasks: <TaskIcon />,
  deviceconfig: <DeviceConfigIcon />,
  debug: <TerminalIcon />,
  sms: <SmsIcon />,
  signalcoverage: <SignalCoverageIcon />,
  partscatalog: <PartsCatalogIcon />,
};

// localStorage key for the super admin's per-department minimize/maximize.
const DEPT_NAV_STATE_KEY = 'deptNavState';

const Sidebar = () => {
  const theme = useTheme();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Scroll state for nav arrows
  const navScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollability = useCallback(() => {
    const el = navScrollRef.current;
    if (el) {
      const sl = Math.round(el.scrollLeft);
      const sw = el.scrollWidth;
      const cw = el.clientWidth;
      setCanScrollLeft(sl > 2);
      setCanScrollRight(sw > cw + 2 && sl + cw < sw - 2);
    }
  }, []);

  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    checkScrollability();
    const t1 = setTimeout(checkScrollability, 100);
    const t2 = setTimeout(checkScrollability, 500);
    el.addEventListener('scroll', checkScrollability);
    window.addEventListener('resize', checkScrollability);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(checkScrollability);
      ro.observe(el);
    }
    return () => {
      el.removeEventListener('scroll', checkScrollability);
      window.removeEventListener('resize', checkScrollability);
      clearTimeout(t1);
      clearTimeout(t2);
      if (ro) ro.disconnect();
    };
  }, [checkScrollability]);

  const scrollNav = (direction) => {
    const el = navScrollRef.current;
    if (el) {
      el.scrollBy({ left: direction === 'right' ? 250 : -250, behavior: 'smooth' });
    }
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setIsDrawerOpen(open);
  };

  const isSuperAdmin = checkSuperAdmin(user);

  // The 5-department top bar (+ the trailing Super Admin / Procurement / HR /
  // Config groups holding everything that is not a department page).
  //
  // A DEPARTMENT NAME in `accessables` ("operations", "sales", …) gives the
  // user that department and shows ONLY it — every header under it opens, and
  // no other department appears. The trailing groups are filtered on OUTRIGHT
  // keys, so a department-only user gets their department and nothing else,
  // while someone also given e.g. "chat" keeps it.
  //
  // Users granted the old way (page keys, no department name) are unaffected:
  // each department shows whatever headers they can open, same as before.
  const grantedDepts = useMemo(() => userDepartments(user), [user]);
  const groups = useMemo(() => {
    const visible = [];
    const deptScoped = grantedDepts.length > 0 && !isSuperAdmin;
    for (const dept of DEPARTMENTS) {
      if (deptScoped && !grantedDepts.includes(dept.key)) continue;   // not theirs
      const items = dept.items.filter(i => canViewPath(user, pathnameOf(i.path)));
      if (items.length) visible.push({ ...dept, items });
      else if (dept.comingSoon && (isSuperAdmin || grantedDepts.includes(dept.key)))
        visible.push({ ...dept, items: [] });
    }
    // The non-department groups (Super Admin / Procurement / HR / Config).
    // Filtered per ITEM exactly as the single Workspace run was, so splitting
    // the bar by purpose cannot widen or narrow what anyone can reach; a group
    // whose pages the user holds none of simply does not appear.
    for (const ws of WORKSPACE_GROUPS) {
      const items = ws.items.filter(i =>
        deptScoped
          ? userHasExplicitPage(user, pageKeyForPath(pathnameOf(i.path)))
          : canViewPath(user, pathnameOf(i.path)));
      if (items.length) visible.push({ ...ws, items });
    }
    // Own department(s) first — granted ones, else the profile's `department`;
    // among several granted, the profile's `department` leads.
    const own = userDeptKeys(user);
    const mine = grantedDepts.length ? grantedDepts : own;
    const rank = (g) => (own.includes(g.key) ? 2 : 0) + (mine.includes(g.key) ? 1 : 0);
    return visible.sort((a, b) => rank(b) - rank(a));
  }, [user, isSuperAdmin, grantedDepts]);

  // Super admin sees every department, so each group gets minimize/maximize.
  // Default: only the department you are inside is open; choices persist.
  const [deptState, setDeptState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DEPT_NAV_STATE_KEY) || '{}'); } catch { return {}; }
  });
  const toggleDept = (key, currentlyExpanded) => {
    setDeptState(prev => {
      const next = { ...prev, [key]: currentlyExpanded ? 'min' : 'max' };
      try { localStorage.setItem(DEPT_NAV_STATE_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  };
  const isGroupExpanded = (group, containsActive) => {
    if (!isSuperAdmin) return true;               // few groups → always open
    const saved = deptState[group.key];
    if (saved === 'min') return false;
    if (saved === 'max') return true;
    // Departments start closed unless you are inside one (there are five of
    // them, each a long run). The trailing groups are short and declare
    // themselves open, so the pages they hold stay one click away.
    return group.defaultOpen || containsActive;
  };

  // Expanding/collapsing changes the nav's scroll width — re-check the arrows.
  useEffect(() => { checkScrollability(); }, [deptState, groups, checkScrollability]);

  const secondaryNavItems = [
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings', hidden: !isSuperAdmin },
  ].filter(item => !item.hidden);



  const handleLogout = () => {
    setIsDrawerOpen(false);
    logout();
  };

  const userInitials = user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'JD';

  return (
    <Paper
      className="glass"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        borderRadius: 0,
        borderBottom: '1px solid rgba(226, 232, 240, 0.4)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 1.5,
        }}
      >
        {/* Logo - always visible */}
        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', letterSpacing: -1, display: 'flex', alignItems: 'center', gap: 1 }}>
            MTANDT <span style={{ color: '#cbd5e1', fontWeight: 300, fontSize: '18px' }}>/</span> IIoT
          </Typography>
        </Box>

        {/* Left scroll arrow */}
        {canScrollLeft && (
          <IconButton
            onClick={() => scrollNav('left')}
            size="small"
            sx={{
              flexShrink: 0,
              mr: 0.5,
              color: 'primary.main',
              backgroundColor: 'rgba(37, 99, 235, 0.08)',
              '&:hover': { backgroundColor: 'rgba(37, 99, 235, 0.16)' },
              width: 28,
              height: 28,
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}

        {/* Scrollable Nav Items Wrapper */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          flex: '1 1 0%',
          minWidth: 0,
          overflow: 'hidden',
          position: 'relative',
        }}>
          {/* Scrollable area */}
          <Box
            ref={navScrollRef}
            sx={{
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              pr: canScrollRight ? '36px' : 0,
            }}
          >
            {groups.map((group, gi) => {
              const containsActive = group.items.some(i => isItemOnPath(location, i.path));
              const expanded = isGroupExpanded(group, containsActive);
              return (
                <React.Fragment key={group.key}>
                  {gi > 0 && (
                    <Box sx={{ width: '1px', alignSelf: 'stretch', my: 1, bgcolor: 'divider', flexShrink: 0 }} />
                  )}

                  {/* Department chip — for the super admin it is the
                      minimize/maximize control (they see every department). */}
                  <Button
                    size="small"
                    disabled={!!group.comingSoon}
                    onClick={isSuperAdmin && !group.comingSoon
                      ? () => toggleDept(group.key, expanded)
                      : undefined}
                    endIcon={isSuperAdmin && !group.comingSoon
                      ? (expanded ? <MinimizeIcon sx={{ fontSize: 16 }} /> : <MaximizeIcon sx={{ fontSize: 16 }} />)
                      : undefined}
                    sx={{
                      minWidth: 'fit-content',
                      whiteSpace: 'nowrap',
                      textTransform: 'uppercase',
                      px: 1,
                      py: 0.5,
                      borderRadius: '10px',
                      fontSize: '0.68rem',
                      fontWeight: 800,
                      letterSpacing: '0.06em',
                      flexShrink: 0,
                      color: containsActive ? 'primary.main' : 'text.secondary',
                      backgroundColor: containsActive ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
                      border: '1px dashed',
                      borderColor: containsActive ? 'primary.light' : 'divider',
                      pointerEvents: isSuperAdmin && !group.comingSoon ? 'auto' : 'none',
                      '&.Mui-disabled': { color: 'text.disabled', opacity: 0.7, pointerEvents: 'none' },
                    }}
                  >
                    {group.comingSoon ? `${group.label} · soon` : group.label}
                  </Button>

                  {expanded && group.items.map((item) => {
                    // A deeper sibling that also matches wins: on
                    // /device-config/control-panel only Control Panel lights,
                    // not Device Config (/device-config) as well.
                    const isSelected = isItemActive(location, item.path)
                      && !group.items.some(o => o !== item
                        && pathnameOf(o.path).length > pathnameOf(item.path).length
                        && isItemActive(location, o.path));
                    return (
                      <Button
                        key={`${group.key}-${item.text}`}
                        component={Link}
                        to={item.path}
                        startIcon={ICONS[item.icon] || null}
                        variant={isSelected ? 'contained' : 'text'}
                        sx={{
                          minWidth: 'fit-content',
                          whiteSpace: 'nowrap',
                          textTransform: 'none',
                          px: 1,
                          py: 1,
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          fontWeight: isSelected ? 700 : 500,
                          color: isSelected ? 'white' : 'text.secondary',
                          '&:hover': {
                            backgroundColor: isSelected ? 'primary.dark' : 'rgba(37, 99, 235, 0.08)',
                            color: isSelected ? 'white' : 'primary.main',
                          },
                        }}
                      >
                        {item.text}
                      </Button>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {secondaryNavItems.map((item) => {
              const isSelected = location.pathname === item.path;
              return (
                <Button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  startIcon={item.icon}
                  variant={isSelected ? 'contained' : 'text'}
                  sx={{
                    minWidth: 'fit-content',
                    px: 2,
                    py: 1,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: isSelected ? 'white' : 'text.secondary',
                  }}
                >
                  {item.text}
                </Button>
              );
            })}
          </Box>

          {/* Right scroll arrow - positioned at right edge */}
          {canScrollRight && (
            <IconButton
              onClick={() => scrollNav('right')}
              size="small"
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 3,
                color: 'primary.main',
                backgroundColor: 'background.paper',
                boxShadow: '-8px 0 12px 4px rgba(255,255,255,0.9)',
                '&:hover': { backgroundColor: 'rgba(37, 99, 235, 0.12)' },
                width: 30,
                height: 30,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>

        {/* App-wide pages every user can open: the per-department user guide
            and the creator's news feed. */}
        <Tooltip title="How to use this application">
          <IconButton component={Link} to="/how-to-use" size="small"
            sx={{
              flexShrink: 0, mr: 0.5,
              color: location.pathname === '/how-to-use' ? 'primary.main' : 'text.secondary',
              '&:hover': { color: 'primary.main', backgroundColor: 'rgba(37, 99, 235, 0.08)' },
            }}>
            <HowToIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="News from the creator">
          <IconButton component={Link} to="/creator-news" size="small"
            sx={{
              flexShrink: 0, mr: 1,
              color: location.pathname === '/creator-news' ? 'primary.main' : 'text.secondary',
              '&:hover': { color: 'primary.main', backgroundColor: 'rgba(37, 99, 235, 0.08)' },
            }}>
            <CreatorNewsIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        <Avatar
          onClick={toggleDrawer(true)}
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.light',
            fontSize: '0.875rem',
            fontWeight: 700,
            cursor: 'pointer',
            border: '2px solid transparent',
            '&:hover': { borderColor: 'primary.main' }
          }}
        >
          {userInitials}
        </Avatar>

        <Drawer
          anchor="right"
          open={isDrawerOpen}
          onClose={toggleDrawer(false)}
          PaperProps={{
            sx: {
              width: 320,
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: 16,
              p: 0,
            }
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={700}>Profile</Typography>
            <IconButton onClick={toggleDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.light',
                fontSize: '2rem',
                fontWeight: 700,
                mb: 2,
                boxShadow: 3
              }}
            >
              {userInitials}
            </Avatar>
            <Typography variant="h6" fontWeight={700}>{user?.name || 'User'}</Typography>
            <Typography variant="body2" color="text.secondary">{user?.email || ''}</Typography>
          </Box>

          <Divider />

          <List sx={{ px: 2 }}>
            <ListItem component={Link} to="/profile" onClick={toggleDrawer(false)} sx={{ borderRadius: 2, mb: 1, '&:hover': { bgcolor: 'action.hover' } }}>
              <ListItemIcon>
                <PersonIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="My Profile" secondary="Your account details" />
            </ListItem>

            <ListItem component={Link} to="/how-to-use" onClick={toggleDrawer(false)} sx={{ borderRadius: 2, mb: 1, '&:hover': { bgcolor: 'action.hover' } }}>
              <ListItemIcon>
                <HowToIcon color="action" />
              </ListItemIcon>
              <ListItemText primary="How to use" secondary="Your department's guide" />
            </ListItem>

            <ListItem component={Link} to="/creator-news" onClick={toggleDrawer(false)} sx={{ borderRadius: 2, mb: 1, '&:hover': { bgcolor: 'action.hover' } }}>
              <ListItemIcon>
                <CreatorNewsIcon color="action" />
              </ListItemIcon>
              <ListItemText primary="News from the creator" secondary="Updates about the app" />
            </ListItem>

            {isSuperAdmin && (
              <ListItem component={Link} to="/settings" onClick={toggleDrawer(false)} sx={{ borderRadius: 2, mb: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                <ListItemIcon>
                  <SettingsIcon color="action" />
                </ListItemIcon>
                <ListItemText primary="Settings" secondary="App configuration" />
              </ListItem>
            )}
          </List>

          <Box sx={{ mt: 'auto', p: 2 }}>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{ borderRadius: 2, py: 1 }}
            >
              Logout
            </Button>
          </Box>
        </Drawer>
      </Box>
    </Paper>
  );
};

export default Sidebar;
